import { Link } from 'react-router-dom'
import { LanguageSwitcher } from '../components/i18n/LanguageSwitcher'
import { useI18n } from '../i18n'
import type { MessageKey } from '../i18n/messages'
import styles from './UserManual.module.css'

/** Example reference used in the manual demo figure (opens on YouTube). */
const DEMO_YOUTUBE_URL = 'https://www.youtube.com/watch?v=URH_OKRN9bs'

type ManualFigure = {
  src: string
  altKey: MessageKey
  captionKey: MessageKey
  legendKeys: MessageKey[]
  /** When set, the figure image links out (e.g. demo YouTube watch page). */
  href?: string
}

type ManualSection = {
  id: string
  titleKey: MessageKey
  leadKey: MessageKey
  stepKeys: MessageKey[]
  noteKey?: MessageKey
  figures?: ManualFigure[]
  figuresTitleKey?: MessageKey
}

const SECTIONS: ManualSection[] = [
  {
    id: 'overview',
    titleKey: 'manual.overviewTitle',
    leadKey: 'manual.overviewLead',
    stepKeys: ['manual.overviewStep1', 'manual.overviewStep2', 'manual.overviewStep3'],
  },
  {
    id: 'choose-kata',
    titleKey: 'manual.chooseTitle',
    leadKey: 'manual.chooseLead',
    stepKeys: ['manual.chooseStep1', 'manual.chooseStep2', 'manual.chooseStep3'],
  },
  {
    id: 'video',
    titleKey: 'manual.videoTitle',
    leadKey: 'manual.videoLead',
    stepKeys: ['manual.videoStep1', 'manual.videoStep2', 'manual.videoStep3', 'manual.videoStep4'],
    noteKey: 'manual.videoNote',
    figuresTitleKey: 'manual.demoFiguresTitle',
    figures: [
      {
        src: '/manual/demo-youtube.jpg',
        href: DEMO_YOUTUBE_URL,
        altKey: 'manual.figDemoAlt',
        captionKey: 'manual.figDemoCaption',
        legendKeys: ['manual.figDemoL1', 'manual.figDemoL2'],
      },
    ],
  },
  {
    id: 'anchors',
    titleKey: 'manual.anchorsTitle',
    leadKey: 'manual.anchorsLead',
    stepKeys: [
      'manual.anchorsStep1',
      'manual.anchorsStep2',
      'manual.anchorsStep3',
      'manual.anchorsStep4',
      'manual.anchorsStep5',
    ],
    noteKey: 'manual.anchorsNote',
    figures: [
      {
        src: '/manual/anchors-01-open-setup.png',
        altKey: 'manual.figOpenAlt',
        captionKey: 'manual.figOpenCaption',
        legendKeys: ['manual.figOpenL1', 'manual.figOpenL2', 'manual.figOpenL3'],
      },
      {
        src: '/manual/anchors-02-configure-video.png',
        altKey: 'manual.figVideoAlt',
        captionKey: 'manual.figVideoCaption',
        legendKeys: ['manual.figVideoL1', 'manual.figVideoL2', 'manual.figVideoL3'],
      },
      {
        src: '/manual/anchors-03-editor-overview.png',
        altKey: 'manual.figEditorAlt',
        captionKey: 'manual.figEditorCaption',
        legendKeys: ['manual.figEditorL1', 'manual.figEditorL2', 'manual.figEditorL3'],
      },
      {
        src: '/manual/anchors-04-mark-and-save.png',
        altKey: 'manual.figMarkAlt',
        captionKey: 'manual.figMarkCaption',
        legendKeys: [
          'manual.figMarkL1',
          'manual.figMarkL2',
          'manual.figMarkL3',
          'manual.figMarkL4',
        ],
      },
    ],
  },
  {
    id: 'data-saver',
    titleKey: 'manual.dataSaverTitle',
    leadKey: 'manual.dataSaverLead',
    stepKeys: [
      'manual.dataSaverStep1',
      'manual.dataSaverStep2',
      'manual.dataSaverStep3',
    ],
    noteKey: 'manual.dataSaverNote',
  },
  {
    id: 'practice',
    titleKey: 'manual.practiceTitle',
    leadKey: 'manual.practiceLead',
    stepKeys: [
      'manual.practiceStep1',
      'manual.practiceStep2',
      'manual.practiceStep3',
      'manual.practiceStep4',
    ],
  },
  {
    id: 'judge',
    titleKey: 'manual.judgeTitle',
    leadKey: 'manual.judgeLead',
    stepKeys: [
      'manual.judgeStep1',
      'manual.judgeStep2',
      'manual.judgeStep3',
      'manual.judgeStep4',
      'manual.judgeStep5',
      'manual.judgeStep6',
    ],
    noteKey: 'manual.judgeNote',
  },
  {
    id: 'notes',
    titleKey: 'manual.notesTitle',
    leadKey: 'manual.notesLead',
    stepKeys: ['manual.notesStep1', 'manual.notesStep2', 'manual.notesStep3'],
  },
  {
    id: 'backup',
    titleKey: 'manual.backupTitle',
    leadKey: 'manual.backupLead',
    stepKeys: ['manual.backupStep1', 'manual.backupStep2', 'manual.backupStep3'],
    noteKey: 'manual.backupNote',
  },
  {
    id: 'install',
    titleKey: 'manual.installTitle',
    leadKey: 'manual.installLead',
    stepKeys: ['manual.installStep1', 'manual.installStep2', 'manual.installStep3'],
    noteKey: 'manual.installNote',
  },
]

export function UserManual() {
  const { t } = useI18n()

  return (
    <div className={styles.page}>
      <header className={styles.hero}>
        <div className={styles.heroAtmosphere} aria-hidden="true">
          <span className={styles.washA} />
          <span className={styles.washB} />
          <span className={styles.washC} />
          <span className={styles.silk} />
        </div>
        <div className={styles.heroInner}>
          <div className={styles.heroTop}>
            <Link to="/" className={styles.backLink}>
              {t('common.back')}
            </Link>
            <LanguageSwitcher />
          </div>
          <p className={styles.eyebrow}>{t('manual.eyebrow')}</p>
          <h1 className={styles.title}>{t('manual.title')}</h1>
          <p className={styles.subtitle}>{t('manual.subtitle')}</p>
        </div>
      </header>

      <div className={styles.body}>
        <nav className={styles.toc} aria-labelledby="manual-toc-heading">
          <h2 id="manual-toc-heading" className={styles.tocTitle}>
            {t('manual.tocTitle')}
          </h2>
          <ol className={styles.tocList}>
            {SECTIONS.map((section, index) => (
              <li key={section.id}>
                <a href={`#${section.id}`}>
                  <span className={styles.tocIndex} aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  {t(section.titleKey)}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className={styles.sections}>
          {SECTIONS.map((section, index) => (
            <section
              key={section.id}
              id={section.id}
              className={styles.section}
              aria-labelledby={`${section.id}-heading`}
            >
              <div className={styles.sectionHeader}>
                <span className={styles.stepNumber} aria-hidden="true">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h2 id={`${section.id}-heading`} className={styles.sectionTitle}>
                  {t(section.titleKey)}
                </h2>
              </div>
              <p className={styles.sectionLead}>{t(section.leadKey)}</p>
              <ol className={styles.steps}>
                {section.stepKeys.map((key) => (
                  <li key={key}>{t(key)}</li>
                ))}
              </ol>
              {section.figures && section.figures.length > 0 ? (
                <div className={styles.figures}>
                  <h3 className={styles.figuresTitle}>
                    {t(section.figuresTitleKey ?? 'manual.figuresTitle')}
                  </h3>
                  {section.figures.map((figure) => {
                    const image = (
                      <img
                        src={figure.src}
                        alt={t(figure.altKey)}
                        className={styles.figureImage}
                        loading="lazy"
                        decoding="async"
                      />
                    )
                    return (
                      <figure key={figure.src} className={styles.figure}>
                        <div className={styles.figureFrame}>
                          {figure.href ? (
                            <a
                              href={figure.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.figureLink}
                            >
                              {image}
                              <span className={styles.figureLinkHint}>
                                {t('common.opensNewTab')}
                              </span>
                            </a>
                          ) : (
                            image
                          )}
                        </div>
                        <figcaption className={styles.figureCaption}>
                          <p className={styles.figureCaptionTitle}>{t(figure.captionKey)}</p>
                          <ol className={styles.figureLegend}>
                            {figure.legendKeys.map((key) => (
                              <li key={key}>{t(key)}</li>
                            ))}
                          </ol>
                        </figcaption>
                      </figure>
                    )
                  })}
                </div>
              ) : null}
              {section.noteKey ? <p className={styles.note}>{t(section.noteKey)}</p> : null}
            </section>
          ))}
        </div>

        <div className={styles.footerLinks}>
          <Link to="/">{t('manual.backToLibrary')}</Link>
          <Link to="/data">{t('manual.openData')}</Link>
        </div>
      </div>
    </div>
  )
}
